import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const initialForm = {
  Code: '',
  Categorie: 'PUBLIC',
  Periodicite: 'TOUS',
  ReductionPourcent: '15',
  UtilisationMax: '1000',
  CompteCibleID: '',
  DateDebut: '',
  DateExpiration: '',
};

const statusStyles = {
  ACTIF: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
  PLANIFIE: 'border-sky-500/30 bg-sky-500/10 text-sky-200',
  EPUISE: 'border-amber-500/30 bg-amber-500/10 text-amber-100',
  EXPIRE: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
  INACTIF: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : 'Sans limite';

const PromotionCodesAdmin = () => {
  const [promotions, setPromotions] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [accountQuery, setAccountQuery] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/promotion-codes');
      setPromotions(response.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Impossible de charger les codes promotionnels.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);

  useEffect(() => {
    if (form.Categorie !== 'COMPTE') return undefined;
    const timeout = window.setTimeout(async () => {
      try {
        const response = await api.get(`/admin/accounts/search?q=${encodeURIComponent(accountQuery)}`);
        setAccounts(response.data || []);
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Impossible de rechercher les comptes.');
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [accountQuery, form.Categorie]);

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'Categorie' && value === 'PUBLIC' ? { CompteCibleID: '' } : {}),
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...form,
        ReductionPourcent: Number(form.ReductionPourcent),
        UtilisationMax: form.Categorie === 'COMPTE' ? 1 : Number(form.UtilisationMax),
        CompteCibleID: form.Categorie === 'COMPTE' ? Number(form.CompteCibleID) : null,
        DateDebut: form.DateDebut || null,
        DateExpiration: form.DateExpiration || null,
      };
      const response = await api.post('/admin/promotion-codes', payload);
      setMessage(response.data?.message || 'Code promotionnel créé.');
      setForm(initialForm);
      setAccountQuery('');
      setAccounts([]);
      await loadPromotions();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Impossible de créer le code promotionnel.');
    } finally {
      setSaving(false);
    }
  };

  const togglePromotion = async (promotion) => {
    setError('');
    setMessage('');
    try {
      const response = await api.put(`/admin/promotion-codes/${promotion.CodePromoID}`, {
        EstActif: !promotion.EstActif,
      });
      setMessage(response.data?.message || 'Code promotionnel mis à jour.');
      await loadPromotions();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Impossible de modifier le code promotionnel.');
    }
  };

  return (
    <section className="rounded-xl border border-fuchsia-500/20 bg-slate-950/70 p-6">
      <div className="flex items-start gap-3">
        <span className="rounded-lg bg-fuchsia-500/10 p-2 text-fuchsia-300"><TagIcon className="size-6" /></span>
        <div>
          <h2 className="text-lg font-semibold text-white">Codes promotionnels</h2>
          <p className="mt-1 text-sm text-slate-400">Créez une remise publique limitée ou un code privé à usage unique pour un compte précis.</p>
        </div>
      </div>

      {message && <p className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{message}</p>}
      {error && <p className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</p>}

      <form onSubmit={submit} className="mt-6 grid gap-4 rounded-xl border border-white/10 bg-slate-900/50 p-5 md:grid-cols-2 xl:grid-cols-4">
        <label className="text-sm font-semibold text-slate-300">Code
          <input value={form.Code} onChange={(event) => updateForm('Code', event.target.value.toUpperCase())} placeholder="NOEL" className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white" required />
        </label>
        <label className="text-sm font-semibold text-slate-300">Catégorie
          <select value={form.Categorie} onChange={(event) => updateForm('Categorie', event.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white">
            <option value="PUBLIC">Public — tous les comptes</option>
            <option value="COMPTE">Privé — un compte précis</option>
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-300">Abonnement ciblé
          <select value={form.Periodicite} onChange={(event) => updateForm('Periodicite', event.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white">
            <option value="TOUS">Mensuel et annuel</option>
            <option value="MENSUEL">Mensuel uniquement</option>
            <option value="ANNUEL">Annuel uniquement</option>
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-300">Réduction (%)
          <input type="number" min="1" max="100" value={form.ReductionPourcent} onChange={(event) => updateForm('ReductionPourcent', event.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white" required />
        </label>

        {form.Categorie === 'PUBLIC' ? (
          <label className="text-sm font-semibold text-slate-300">Nombre maximal d’utilisations
            <input type="number" min="1" max="1000000" value={form.UtilisationMax} onChange={(event) => updateForm('UtilisationMax', event.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white" required />
          </label>
        ) : (
          <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-semibold text-slate-300">Rechercher le compte bénéficiaire
              <span className="relative mt-1 block">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 size-5 text-slate-500" />
                <input value={accountQuery} onChange={(event) => setAccountQuery(event.target.value)} placeholder="Surnom, email, raison sociale ou SIRET" className="w-full rounded-lg border border-white/10 bg-slate-950 py-2 pl-10 pr-3 text-white" />
              </span>
            </label>
            <select value={form.CompteCibleID} onChange={(event) => updateForm('CompteCibleID', event.target.value)} className="w-full rounded-lg border border-fuchsia-500/30 bg-slate-950 px-3 py-2 text-white" required>
              <option value="">Sélectionner un compte dans les résultats</option>
              {accounts.map((account) => <option key={account.CompteID} value={account.CompteID}>{account.Label}</option>)}
            </select>
            <p className="text-xs text-slate-500">Ce code sera utilisable une seule fois, uniquement par le compte sélectionné.</p>
          </div>
        )}

        <label className="text-sm font-semibold text-slate-300">Début facultatif
          <input type="datetime-local" value={form.DateDebut} onChange={(event) => updateForm('DateDebut', event.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white" />
        </label>
        <label className="text-sm font-semibold text-slate-300">Expiration facultative
          <input type="datetime-local" value={form.DateExpiration} onChange={(event) => updateForm('DateExpiration', event.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-white" />
        </label>
        <div className="flex items-end">
          <button type="submit" disabled={saving} className="w-full rounded-lg bg-fuchsia-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-fuchsia-500 disabled:opacity-50">{saving ? 'Création…' : 'Créer le code'}</button>
        </div>
      </form>

      <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
        {loading ? <p className="p-5 text-sm text-slate-400">Chargement des codes…</p> : (
          <table className="min-w-[900px] w-full divide-y divide-white/10 text-sm">
            <thead className="bg-slate-900/80 text-left text-slate-400"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Cible</th><th className="px-4 py-3">Remise</th><th className="px-4 py-3">Utilisations</th><th className="px-4 py-3">Expiration</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Action</th></tr></thead>
            <tbody className="divide-y divide-white/10">
              {promotions.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-center text-slate-500">Aucun code promotionnel.</td></tr>}
              {promotions.map((promotion) => (
                <tr key={promotion.CodePromoID} className="bg-slate-950/40 text-slate-200">
                  <td className="px-4 py-3 font-black text-white">{promotion.Code}</td>
                  <td className="px-4 py-3"><span className="block font-semibold">{promotion.Categorie === 'PUBLIC' ? 'Tous les comptes' : promotion.CompteCible?.Label || 'Compte introuvable'}</span><span className="text-xs text-slate-500">{promotion.Periodicite === 'TOUS' ? 'Mensuel et annuel' : promotion.Periodicite.toLowerCase()}</span></td>
                  <td className="px-4 py-3 font-bold text-fuchsia-200">{promotion.ReductionPourcent} %</td>
                  <td className="px-4 py-3">{promotion.UtilisationCount} / {promotion.UtilisationMax}</td>
                  <td className="px-4 py-3 text-slate-400">{formatDate(promotion.DateExpiration)}</td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${statusStyles[promotion.StatutCalcule] || statusStyles.INACTIF}`}>{promotion.StatutCalcule}</span></td>
                  <td className="px-4 py-3"><button type="button" onClick={() => togglePromotion(promotion)} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold ${promotion.EstActif ? 'border-rose-500/30 text-rose-200 hover:bg-rose-500/10' : 'border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10'}`}>{promotion.EstActif ? <NoSymbolIcon className="size-4" /> : <CheckCircleIcon className="size-4" />}{promotion.EstActif ? 'Désactiver' : 'Activer'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default PromotionCodesAdmin;
